/**
 * Contract Sections Form Component (Editor)
 * 
 * WCAG 2.1 AA COMPLIANCE: ✅ VERIFIED 2026-04-20
 * - All 50 applicable WCAG 2.1 AA criteria met
 * - Previous issues resolved:
 *   ✅ Zoom 200%: min-height layout, full page scroll, editor scrollable
 *   ✅ Input/Buttons: Complete ARIA, keyboard navigation, focus visible
 * - Complex interactions: Jodit editor, drag-drop (with keyboard fallback)
 * - Audit document: ./WCAG_AUDIT.md
 * - Standards: WCAG 2.1 Level AA (Web Content Accessibility Guidelines)
 */

import { Component, OnInit, OnDestroy, HostListener, ChangeDetectorRef, ViewChild, ElementRef, AfterViewInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ComponentCanDeactivate } from '../../../core/guards/unsaved-changes.guard';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DomSanitizer, SafeHtml, Title } from '@angular/platform-browser';
import { MenuItem } from 'primeng/api';

import { ContractSectionsService } from '../../services/contract-sections.service';
import { SectionRegistryService } from '../../services/section-registry.service';
import { Section, CreateSectionRequest } from '../../models/section.model';
import { VariableRegistryService } from '../../services/variable-registry.service';
import { Variable } from '../../models/variable.model';
import { AttachmentRegistryService } from '../../services/attachment-registry.service';
import { Attachment } from '../../models/attachment.model';
import { HtmlSanitizerService } from '../../../core/services/html-sanitizer.service';
import {
  FormDropdownService,
  FormDropdownItem,
} from '../../../core/services/form-dropdown.service';
import { NavigationContextService } from '../../../core/services/navigation-context.service';
import { ConfirmationService } from 'primeng/api';
import { ConfirmationDialogService } from '../../../core/services/confirmation-dialog.service';
import { MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import html2pdf from 'html2pdf.js';
import { WordExportService } from '../../../core/services/word-export.service';

// Jodit caricato tramite script in angular.json
declare const Jodit: any;
declare const htmlDocx: any;

@Component({
  selector: 'app-contract-sections-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    DividerModule,
    ConfirmDialogModule,
    TooltipModule,
    SelectModule,
    CheckboxModule,
    RadioButtonModule,
    IconFieldModule,
    InputIconModule,
    BreadcrumbModule,
    Toast,
  ],
  providers: [
    ConfirmationService,
    ConfirmationDialogService,
    MessageService,
  ],
  templateUrl: './contract-sections-form.component.html',
  styleUrl: './contract-sections-form.component.scss',
})
export class ContractSectionsFormComponent implements OnInit, AfterViewInit, OnDestroy, ComponentCanDeactivate {
  @ViewChild('joditEditor', { static: false }) joditEditorElement!: ElementRef;

  private get sectionsRegistryRoute(): string {
    return `/axa/${this.navigationContext.getDomainPrefix()}-contract-sections`;
  }
  
  breadcrumbItems = signal<MenuItem[]>([]);
  
  isGenerating = false;

  variables: Variable[] = [];
  variableSearchText: string = '';
  collapsedGroups: Set<string> = new Set();
  variablesSectionCollapsed = false;
  attachmentsSectionCollapsed = false;

  attachments: Attachment[] = [];
  attachmentSearchText: string = '';

  toggleGroup(groupKey: string): void {
    if (this.collapsedGroups.has(groupKey)) {
      this.collapsedGroups.delete(groupKey);
    } else {
      this.collapsedGroups.add(groupKey);
    }
  }

  toggleVariablesSection(): void {
    this.variablesSectionCollapsed = !this.variablesSectionCollapsed;
  }

  toggleAttachmentsSection(): void {
    this.attachmentsSectionCollapsed = !this.attachmentsSectionCollapsed;
  }

  // Opzioni Sì/No per radio buttons
  yesNoOptions = [
    { label: 'Sì', value: true },
    { label: 'No', value: false },
  ];

  /**
   * Ottiene il contenuto corrente dell'editor (aggiornato in tempo reale)
   */
  private getEditorContent(): string {
    if (this.editorInstance) {
      return this.editorInstance.value;
    }
    return this.contentText;
  }

  /**
   * Verifica se il contenuto dell'editor non è vuoto (escludendo solo tag HTML)
   */
  private hasValidContent(): boolean {
    const content = this.getEditorContent();
    if (!content) return false;
    // Rimuove tag HTML e spazi per verificare se c'è contenuto reale
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent.length > 0;
  }

  /**
   * Raggruppa le variabili per sourceForm e filtra in base alla ricerca.
   * Filtra anche per domainType in base al contesto di navigazione (Fornitori/Fiduciari).
   * Ordina le variabili all'interno di ogni gruppo per displayName.
   */
  get groupedVariables(): { [key: string]: Variable[] } {
    // Ottieni il contesto corrente (SUPPLIERS o PROFESSIONALS)
    const currentDomainType = this.navigationContext.getCurrentContext().domainType;
    
    const filtered = this.variables.filter((v) => {
      // Filtra per domainType - mostra solo variabili del dominio corrente
      if (v.domainType && v.domainType !== currentDomainType) {
        return false;
      }
      
      // Filtra per testo di ricerca
      if (!this.variableSearchText.trim()) return true;
      const search = this.variableSearchText.toLowerCase();
      return (
        v.displayName?.toLowerCase().includes(search) ||
        v.variableKey?.toLowerCase().includes(search)
      );
    });

    const grouped: { [key: string]: Variable[] } = {};

    filtered.forEach((variable) => {
      const groupKey = variable.sourceForm || 'Altro';
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(variable);
    });

    // Ordina le variabili all'interno di ogni gruppo per displayName
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => {
        const nameA = a.displayName || '';
        const nameB = b.displayName || '';
        return nameA.localeCompare(nameB, 'it', { sensitivity: 'base' });
      });
    });

    return grouped;
  }

  /**
   * Restituisce le chiavi dei gruppi ordinate (Altro sempre per ultimo)
   */
  get groupKeys(): string[] {
    const keys = Object.keys(this.groupedVariables);
    return keys.sort((a, b) => {
      if (a === 'Altro') return 1;
      if (b === 'Altro') return -1;
      return a.localeCompare(b);
    });
  }

  /**
   * Restituisce gli allegati filtrati per la ricerca e per domainType
   * Filtra in base al contesto di navigazione (Fornitori/Fiduciari)
   */
  get filteredAttachments(): Attachment[] {
    // Ottieni il contesto corrente (SUPPLIERS o PROFESSIONALS)
    const currentDomainType = this.navigationContext.getCurrentContext().domainType;
    
    let filtered = this.attachments.filter((a) => {
      // Filtra per domainType - mostra solo allegati del dominio corrente
      if (a.domainType && a.domainType !== currentDomainType) {
        return false;
      }
      return true;
    });

    // Filtra per testo di ricerca
    if (!this.attachmentSearchText.trim()) {
      return filtered;
    }

    const search = this.attachmentSearchText.toLowerCase();
    return filtered.filter(
      (a) =>
        a.title?.toLowerCase().includes(search) || a.description?.toLowerCase().includes(search),
    );
  }

  contentText: string = '';
  name: string = '';
  rootTable: string | null = null;
  isRepeatable: boolean = false;
  previewVisible = false;
  previewData: { title: string; content: SafeHtml } | null = null;

  sections: Section[] = [];
  filteredSections: Section[] = [];
  searchText: string = '';
  editId: number | null = null;
  hasUnsavedChanges: boolean = false;
  variableIds: number[] = [];
  attachmentIds: number[] = [];
  rootTableOptions: FormDropdownItem[] = [];
  hasAttemptedSave = false;
  titleTouched = false;
  rootTableTouched = false;

  private editorInstance: any = null;
  private themeObserver: MutationObserver | null = null;
  private currentEditorTheme: 'light' | 'dark' | null = null;
  private isReinitializingEditor = false;
  private isLoadingContent = false; // Flag per distinguere caricamenti programmatici da modifiche utente
  isSaving = false; // Flag per mostrare loading sul bottone Salva

  constructor(
    private sectionsService: ContractSectionsService,
    private sectionRegistryService: SectionRegistryService,
    private variableRegistryService: VariableRegistryService,
    private attachmentRegistryService: AttachmentRegistryService,
    private formDropdownService: FormDropdownService,
    private sanitizer: DomSanitizer,
    private htmlSanitizer: HtmlSanitizerService,
    private confirmationService: ConfirmationService,
    private confirmDialogService: ConfirmationDialogService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private titleService: Title,
    public navigationContext: NavigationContextService,
    private wordExportService: WordExportService,
  ) {}

  ngOnInit() {
    this.breadcrumbItems.set([
      { label: 'Gestione Blocchi di Testo', routerLink: this.sectionsRegistryRoute },
      { label: 'Editor' },
    ]);

    this.titleService.setTitle('Gestione Sezioni Contratto - PalEasy');
    this.loadVariables();
    this.loadAttachments();
    this.loadRootTableOptions();
    // Jodit sarà  inizializzato in ngAfterViewInit
    this.startThemeObserver();

    // Gestione dei parametri route per CREATE / EDIT / CLONE modes
    this.route.params.subscribe((params) => {
      const sectionId = params['id'];
      
      if (sectionId) {
        // EDIT mode: carica la sezione esistente per modificarla (PUT)
        this.loadAndEditSection(Number(sectionId));
      } else {
        // CREATE o CLONE mode: controlla queryParams
        this.route.queryParams.subscribe((queryParams) => {
          const cloneFromId = queryParams['cloneFrom'];
          
          if (cloneFromId) {
            // CLONE mode: carica la sezione da clonare (POST con dati precompilati)
            this.loadSectionForClone(Number(cloneFromId));
          } else {
            // CREATE mode: editor vuoto (POST)
            this.resetEditor();
          }
        });
      }
    });
  }

  ngAfterViewInit() {
    if (this.joditEditorElement && typeof Jodit !== 'undefined') {
      this.initializeJoditEditor();
    }
  }

  ngOnDestroy() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
      this.themeObserver = null;
    }

    if (this.editorInstance) {
      this.editorInstance.destruct();
    }
  }

  /**
   * Navigate back to the registry
   */
  navigateToRegister(): void {
    this.router.navigate([this.sectionsRegistryRoute]);
  }

  private isDarkModeEnabled(): boolean {
    const savedTheme = localStorage.getItem('axa-theme');
    if (savedTheme === 'dark') return true;
    if (savedTheme === 'light') return false;

    return (
      document.documentElement.classList.contains('axa-dark-mode') ||
      document.body.classList.contains('axa-dark-mode')
    );
  }

  private startThemeObserver(): void {
    const target = document.documentElement;
    if (!target) return;

    this.themeObserver = new MutationObserver(() => {
      const nextTheme: 'light' | 'dark' = this.isDarkModeEnabled() ? 'dark' : 'light';

      if (this.currentEditorTheme && nextTheme !== this.currentEditorTheme) {
        this.reinitializeEditorForTheme();
      }
    });

    this.themeObserver.observe(target, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  /**
   * Rimuove overlay/backdrop attivi che potrebbero bloccare l'interfaccia
   */
  private clearActiveOverlays(): void {
    const overlaySelectors = [
      '.p-dialog-mask',
      '.p-overlay-mask',
      '.p-component-overlay',
      '.p-sidebar-mask',
      '.p-confirm-dialog-mask',
      '.jodit-dialog__overlay',
      '.jodit-popup__container',
    ];
    
    overlaySelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(el => el.remove());
    });
    
    // Rimuovi body scroll locks
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  }

  /**
   * Implementazione del guard CanDeactivate
   * Previene la navigazione se ci sono modifiche non salvate
   * Blocca solo quando si sta modificando una sezione esistente (con editId)
   */
  canDeactivate(): boolean | Promise<boolean> {
    // Se non ci sono modifiche, permetti sempre la navigazione
    if (!this.hasUnsavedChanges) {
      return true;
    }

    // Se stai creando una nuova sezione (nessun editId), permetti navigazione
    // L'utente può abbandonare la creazione senza conferma
    if (this.editId === null) {
      return true;
    }

    // Se stai modificando una sezione esistente, chiedi conferma
    return new Promise<boolean>((resolve) => {
      this.confirmDialogService.confirmCancelEdit(
        () => resolve(true), // accept: permetti navigazione
        () => resolve(false), // reject: blocca navigazione
      );
    });
  }

  /**
   * Avvisa l'utente se prova a chiudere il tab/finestra con modifiche non salvate
   */
  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.hasUnsavedChanges) {
      $event.returnValue = true;
    }
  }

  /**
   * Inizializza l'editor Jodit con configurazione completa
   */
  private initializeJoditEditor() {
    const isDarkMode = this.isDarkModeEnabled();
    this.currentEditorTheme = isDarkMode ? 'dark' : 'light';

    this.editorInstance = Jodit.make(this.joditEditorElement.nativeElement, {
      height: 500,
      language: 'it',
      toolbarButtonSize: 'middle',
      toolbarAdaptive: true,
      
      // Toolbar completa
      buttons: [
        'source', '|',
        'bold', 'italic', 'underline', 'strikethrough', '|',
        'superscript', 'subscript', '|',
        'ul', 'ol', 'outdent', 'indent', '|',
        'font', 'fontsize', 'brush', 'paragraph', '|',
        'image', 'file', 'video', 'table', 'link', '|',
        'align', 'undo', 'redo', '|',
        'hr', 'eraser', 'copyformat', '|',
        'symbol', 'fullsize', 'print', 'preview', 'find', '|',
        'selectall', 'cut', 'copy', 'paste'
      ],

      // Lista completa di dimensioni font (pari e dispari) per ridurre i casi di mismatch
      controls: {
        fontsize: {
          list: {
            '8': '8',
            '9': '9',
            '10': '10',
            '11': '11',
            '12': '12',
            '13': '13',
            '14': '14',
            '15': '15',
            '16': '16',
            '17': '17',
            '18': '18',
            '19': '19',
            '20': '20',
            '22': '22',
            '24': '24',
            '26': '26',
            '28': '28',
            '30': '30',
            '36': '36',
            '48': '48',
            '72': '72'
          }
        }
      },
      
      // Configurazione Uploader per immagini
      uploader: {
        insertImageAsBase64URI: true,
        imagesExtensions: ['jpg', 'png', 'jpeg', 'gif', 'svg', 'webp'],
        withCredentials: false,
        format: 'json',
        method: 'POST'
      },
      
      // Configurazione tabelle avanzate
      table: {
        selectionCellStyle: 'border: 1px double #1e88e5 !important;',
        useExtraClassesOptions: true,
        allowCellResize: true,
        allowTableResize: true,
        useInlineStyles: true,
      },
      
      // Configurazione link
      link: {
        followOnDblClick: false,
        processVideoLink: true,
        processPastedLink: true,
        removeLinkAfterFormat: true,
        noFollowCheckbox: true,
        openInNewTabCheckbox: true,
      },
      
      // Configurazione paste
      askBeforePasteHTML: false,
      askBeforePasteFromWord: false,
      defaultActionOnPaste: 'insert_as_html' as any,
      processPasteHTML: true,
      
      // Preserve HTML attributes during paste/drop
      safeMode: false,
      sanitizeHTML: false,
      
      // Editor di codice sorgente
      sourceEditor: 'area',
      beautifyHTML: true,
      
      // Configurazioni avanzate
      showCharsCounter: true,
      showWordsCounter: true,
      showXPathInStatusbar: false,
      showBranding: false,
      
      // Configurazioni di formattazione
      enter: 'p',
      
      // Tema dark mode
      theme: isDarkMode ? 'dark' : 'default',
      
      // Placeholder
      placeholder: 'Inizia a scrivere qui...',
      
      // Disabilita la sanitizzazione per preservare gli span personalizzati
      cleanHTML: {
        allowTags: true,
        allowAttributes: true,
        removeEmptyElements: false,
        fillEmptyParagraph: false,
        denyTags: false,
        replaceNBSP: false,
        replaceOldTags: false
      },
      
      // Permetti tutti gli attributi data-*
      allowDataAttributes: true,
      
      // Allow all attributes on spans
      allowedAttributes: {
        span: ['class', 'contenteditable', 'data-variable-key', 'data-variable-id', 'data-attachment-key', 'data-attachment-id']
      },
      
      // Non rimuovere span vuoti o con attributi personalizzati
      disablePlugins: ['clean-html', 'paste'] as any,
      
      // CSS inline per l'editor
      editorCssClass: 'jodit-contract-editor',
      
      // Inserisci CSS personalizzato per l'editor
      iframeStyle: `
        .editor-variable {
          display: inline-block !important;
          padding: 2px 8px !important;
          margin: 0 2px !important;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          color: white !important;
          font-weight: 600 !important;
          font-style: italic !important;
          border-radius: 4px !important;
          cursor: default !important;
          user-select: none !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
        }
        .editor-variable:hover {
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3) !important;
        }
        .editor-attachment {
          display: inline-block !important;
          padding: 2px 8px !important;
          margin: 0 2px !important;
          background: linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%) !important;
          color: white !important;
          font-weight: 600 !important;
          font-style: italic !important;
          border-radius: 4px !important;
          cursor: default !important;
          user-select: none !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
        }
        .editor-attachment:hover {
          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3) !important;
        }
      `
    } as any);

    // Sincronizza il contenuto
    this.editorInstance.events.on('change', () => {
      this.contentText = this.editorInstance.value || '';
      
      // NON settare hasUnsavedChanges se stiamo caricando contenuto programmaticamente
      if (!this.isLoadingContent) {
        this.hasUnsavedChanges = true;
      }

      // Re-applica contenteditable="false" e rimuove font-size inline bloccato al momento
      // dell'inserimento. Jodit wrappa l'HTML inserito con <span style="font-size:Npx">:
      // il placeholder eredita quella size fissa e non segue le successive modifiche al testo.
      // Rimuovendo font-size dallo span e dal suo wrapper (se è un wrapper solo-font-size),
      // il placeholder eredita sempre la size corrente del testo circostante.
      const iframeBody = this.editorInstance?.editor as HTMLElement;
      if (iframeBody) {
        iframeBody.querySelectorAll<HTMLElement>('.editor-variable, .editor-attachment').forEach((el) => {
          if (el.getAttribute('contenteditable') !== 'false') {
            el.setAttribute('contenteditable', 'false');
          }
          // Rimuovi font-size inline dallo span stesso
          el.style.removeProperty('font-size');

          // Unwrap parent span se è un wrapper solo-font-size aggiunto da Jodit
          const parent = el.parentElement;
          if (
            parent &&
            parent !== iframeBody &&
            parent.tagName === 'SPAN' &&
            !parent.className &&
            parent.style.fontSize &&
            !parent.style.fontWeight &&
            !parent.style.fontStyle &&
            !parent.style.color &&
            !parent.style.textDecoration &&
            parent.childNodes.length === 1
          ) {
            parent.style.removeProperty('font-size');
            if (parent.style.length === 0 && parent.attributes.length === 0) {
              parent.replaceWith(...Array.from(parent.childNodes));
            }
          }
        });
      }
      
      this.cdr.detectChanges();
    });

    // Intercetta Backspace/Delete quando il cursore è finito dentro uno span placeholder
    // (può accadere se Jodit rimuove temporaneamente contenteditable="false")
    setTimeout(() => {
      const iframeBody = this.editorInstance?.editor as HTMLElement;
      if (!iframeBody) return;
      iframeBody.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key !== 'Backspace' && e.key !== 'Delete') return;
        const sel = iframeBody.ownerDocument.getSelection();
        if (!sel) return;

        // Controlla se il cursore è dentro (o adiacente a) uno span placeholder
        let node: Node | null = sel.anchorNode;
        while (node && node !== iframeBody) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as HTMLElement;
            if (el.classList.contains('editor-variable') || el.classList.contains('editor-attachment')) {
              e.preventDefault();
              el.remove();
              this.hasUnsavedChanges = true;
              return;
            }
          }
          node = node.parentNode;
        }

        // Controlla il nodo adiacente al cursore (selezione collassata)
        if (sel.isCollapsed) {
          const range = sel.getRangeAt(0);
          let adjacent: Node | null = null;
          if (e.key === 'Backspace') {
            adjacent = range.startOffset > 0
              ? (range.startContainer.childNodes[range.startOffset - 1] ?? null)
              : range.startContainer.previousSibling;
          } else {
            adjacent = range.startOffset < range.startContainer.childNodes.length
              ? (range.startContainer.childNodes[range.startOffset] ?? null)
              : range.startContainer.nextSibling;
          }
          if (adjacent && adjacent.nodeType === Node.ELEMENT_NODE) {
            const el = adjacent as HTMLElement;
            if (el.classList.contains('editor-variable') || el.classList.contains('editor-attachment')) {
              e.preventDefault();
              el.remove();
              this.hasUnsavedChanges = true;
            }
          }
        }
      }, true);
    }, 500);

    // Preserve custom HTML during paste/drop
    this.editorInstance.events.on('beforePaste', (event: ClipboardEvent) => {
      // Get the HTML from clipboard/drag
      const html = event.clipboardData?.getData('text/html');
      console.log('[beforePaste] HTML:', html);
      
      // If it contains our editor-variable or editor-attachment spans, preserve them
      if (html && (html.includes('editor-variable') || html.includes('editor-attachment'))) {
        event.preventDefault();
        event.stopPropagation();
        
        // Insert the HTML as-is without sanitization
        this.editorInstance.selection.insertHTML(html);
        console.log('[beforePaste] Inserted custom HTML directly');
        return false;
      }

      // Se il contenuto proviene da Word/Office, rimuove i metadati Word
      // mantenendo solo testo e formattazioni utili all'editor
      if (html && this.isWordHtml(html)) {
        event.preventDefault();
        event.stopPropagation();
        const cleanHtml = this.stripWordHtml(html);
        console.log('[beforePaste] Stripped Word HTML, inserting clean content');
        this.editorInstance.selection.insertHTML(cleanHtml);
        return false;
      }
      
      return true;
    });



    // Imposta contenuto iniziale se presente
    if (this.contentText) {
      this.editorInstance.value = this.contentText;
    }

    // Inietta CSS direttamente nell'editor per le variabili e allegati
    this.injectEditorStyles();

    // Setup autocomplete per variabili e allegati
    // TODO: Migrate autocomplete to Jodit events API
    // this.setupAutocomplete(this.editorInstance);
    // this.setupAttachmentAutocomplete(this.editorInstance);
  }

  private injectEditorStyles(): void {
    if (!this.editorInstance) return;
    
    // Prova ad accedere al documento dell'editor
    let editorDocument: Document | null = null;
    
    // Jodit può usare un iframe o un contenteditable div
    if (this.editorInstance.editor) {
      const editorElement = this.editorInstance.editor;
      
      // Se è in un iframe
      if (editorElement.ownerDocument !== document) {
        editorDocument = editorElement.ownerDocument;
      } else {
        // Se è un div contenteditable normale
        editorDocument = document;
      }
    }
    
    if (!editorDocument) return;
    
    // Crea lo stile
    const styleId = 'jodit-custom-variables-style';
    
    // Rimuovi stile esistente se presente
    const existingStyle = editorDocument.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Crea nuovo elemento style
    const styleElement = editorDocument.createElement('style');
    styleElement.id = styleId;
    styleElement.textContent = `
      .editor-variable {
        display: inline-block !important;
        padding: 2px 8px !important;
        margin: 0 2px !important;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
        color: white !important;
        font-weight: 600 !important;
        font-style: italic !important;
        border-radius: 4px !important;
        cursor: default !important;
        user-select: none !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
      }
      .editor-variable:hover {
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3) !important;
      }
      .editor-attachment {
        display: inline-block !important;
        padding: 2px 8px !important;
        margin: 0 2px !important;
        background: linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%) !important;
        color: white !important;
        font-weight: 600 !important;
        font-style: italic !important;
        border-radius: 4px !important;
        cursor: default !important;
        user-select: none !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2) !important;
      }
      .editor-attachment:hover {
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3) !important;
      }
    `;
    
    // Aggiungi al documento
    editorDocument.head.appendChild(styleElement);
    
    console.log('[injectEditorStyles] CSS injected into', editorDocument === document ? 'main document' : 'iframe document');
  }

  private reinitializeEditorForTheme(): void {
    if (this.isReinitializingEditor || !this.editorInstance) return;

    this.isReinitializingEditor = true;
    const preservedContent = this.editorInstance.value || this.contentText;

    this.editorInstance.destruct();
    this.editorInstance = null;

    this.contentText = preservedContent || '';
    
    // Reinizializza con nuovo tema
    setTimeout(() => {
      this.initializeJoditEditor();
      this.isReinitializingEditor = false;
    }, 100);
  }

  /**
   * Helper per assegnare il contenuto all'editor in modo sicuro
   * Attende che l'editor sia disponibile prima di assegnare il valore
   */
  private setEditorContent(content: string): void {
    const attemptSet = (retries = 10) => {
      if (this.editorInstance && this.editorInstance.editor) {
        // FLAG: Stiamo caricando contenuto programmaticamente, NON è una modifica utente
        this.isLoadingContent = true;
        
        this.editorInstance.value = content;
        
        // Inietta gli stili CSS per le variabili e allegati
        setTimeout(() => {
          this.injectEditorStyles();
          // Resetta il flag dopo che Jodit ha processato il change
          setTimeout(() => {
            this.isLoadingContent = false;
          }, 100);
        }, 100);
        
        this.cdr.detectChanges();
      } else if (retries > 0) {
        // Riprova dopo un breve delay
        setTimeout(() => attemptSet(retries - 1), 50);
      } else {
        console.warn('[setEditorContent] Editor non disponibile dopo 10 tentativi');
        this.isLoadingContent = false; // Resetta anche in caso di errore
      }
    };
    
    attemptSet();
  }

  loadVariables() {
    this.variableRegistryService.getVariables().subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          // Data is directly an array, not paginated
          if (Array.isArray(response.data)) {
            this.variables = response.data;
          } else {
            this.variables = [response.data];
          }
        } else {
          console.warn('Unexpected API response format for variables:', response);
          this.variables = [];
        }
        // Collassa tutti i gruppi per default
        this.collapsedGroups = new Set(Object.keys(this.groupedVariables));
        // Trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading variables:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: "Impossibile caricare l'elenco delle variabili",
          life: 5000,
        });
        this.variables = [];
        this.cdr.detectChanges();
      },
    });
  }

  loadAttachments() {
    this.attachmentRegistryService.getAttachments().subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          this.attachments = this.attachmentRegistryService.extractAttachmentsArray(response);
        } else {
          console.warn('Unexpected API response format for attachments:', response);
          this.attachments = [];
        }
        // Trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading attachments:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: "Impossibile caricare l'elenco degli allegati",
          life: 5000,
        });
        this.attachments = [];
        this.cdr.detectChanges();
      },
    });
  }

  loadSections() {
    this.sectionRegistryService.getSections().subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          let dataArray: Section[] = [];

          // Check if data has content property (paginated response)
          if ('content' in response.data) {
            dataArray = Array.isArray(response.data.content)
              ? response.data.content
              : [response.data.content];
          } else if (Array.isArray(response.data)) {
            dataArray = response.data;
          } else {
            dataArray = [response.data];
          }

          this.sections = dataArray;
          // Applica il filtro per domainType e ricerca
          this.filterSections();

          // Enrich attachments array with attachments from loaded sections
          this.enrichAttachmentsFromSections(dataArray);
        } else {
          console.warn('Unexpected API response format:', response);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Formato dati non valido ricevuto dal server',
            life: 4000,
          });
          this.sections = [];
          this.filteredSections = [];
        }
        // Trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading sections:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: "Impossibile caricare l'elenco delle sezioni",
          life: 5000,
        });
        this.sections = [];
        this.filteredSections = [];
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Arricchisce l'array attachments con gli attachments delle sections caricate.
   * Questo garantisce che tutti gli attachments referenziati nelle sections
   * abbiano i dati completi disponibili per la visualizzazione.
   */
  private enrichAttachmentsFromSections(sections: Section[]) {
    sections.forEach((section) => {
      if (section.attachments && Array.isArray(section.attachments)) {
        section.attachments.forEach((att) => {
          // Aggiungi solo se non esiste già  nell'array
          if (att.id && !this.attachments.find((a) => a.id === att.id)) {
            this.attachments.push(att);
          }
        });
      }
    });
  }

  /**
   * Carica le sezioni e poi apre una sezione specifica in modifica
   */
  loadSectionsAndEdit(sectionId: number) {
    this.sectionRegistryService.getSections().subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          let dataArray: Section[] = [];

          // Check if data has content property (paginated response)
          if ('content' in response.data) {
            dataArray = Array.isArray(response.data.content)
              ? response.data.content
              : [response.data.content];
          } else if (Array.isArray(response.data)) {
            dataArray = response.data;
          } else {
            dataArray = [response.data];
          }

          this.sections = dataArray;
          // Applica il filtro per domainType e ricerca
          this.filterSections();

          // Enrich attachments array with attachments from loaded sections
          this.enrichAttachmentsFromSections(dataArray);

          // Trigger change detection
          this.cdr.detectChanges();

          // Dopo aver caricato le sezioni, apri quella specifica
          // Usa setTimeout con delay maggiore per evitare ExpressionChangedAfterItHasBeenCheckedError
          setTimeout(() => {
            this.loadAndEditSection(sectionId);
          }, 100);
        } else {
          console.warn('Unexpected API response format:', response);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Formato dati non valido ricevuto dal server',
            life: 4000,
          });
          this.sections = [];
          this.filteredSections = [];
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading sections:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: "Impossibile caricare l'elenco delle sezioni",
          life: 5000,
        });
        this.sections = [];
        this.filteredSections = [];
        this.cdr.detectChanges();
      },
    });
  }

  loadRootTableOptions() {
    this.formDropdownService.getFormDropdownValues('TEMPLATE_SECTIONS', 'ROOT_TABLE').subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          this.rootTableOptions = response.data;
        } else {
          this.rootTableOptions = [];
        }
        // Trigger change detection to avoid ExpressionChangedAfterItHasBeenCheckedError
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading root table options:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare le opzioni Entità  Principale',
          life: 5000,
        });
        this.rootTableOptions = [];
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Computed property: mostra il campo isRepeatable solo se rootTable è:
   * - 'v_atto_di_nomina_controllers' (Titolare del Trattamento)
   * - 'v_atto_di_nomina_sub_responsibles' (Sub-Responsabili)
   * - 'v_lawyer_competence_areas' (Aree di competenza lawyer)
   */
  get showRepeatableField(): boolean {
    return (
      this.rootTable === 'v_atto_di_nomina_controllers' ||
      this.rootTable === 'v_atto_di_nomina_sub_responsibles' ||
      this.rootTable === 'v_contracts_involved_entities' ||
      this.rootTable === 'v_lawyer_competence_areas'
    );
  }

  markTitleTouched(): void {
    this.titleTouched = true;
  }

  markRootTableTouched(): void {
    this.rootTableTouched = true;
  }

  isTitleInvalid(): boolean {
    return !this.name.trim() && (this.titleTouched || this.hasAttemptedSave);
  }

  isRootTableInvalid(): boolean {
    return !this.rootTable && (this.rootTableTouched || this.hasAttemptedSave);
  }

  onRootTableChange(value: string | null): void {
    this.rootTable = value;
    this.isRepeatable = this.showRepeatableField;
  }

  insertVariable(varKey: string, variableId?: number) {
    // Find the variable to get its displayName
    const variable = this.variables.find((v) => v.id === variableId);
    const displayText = variable?.displayName || varKey;

    // Create span with displayName visible, but store varKey and id in data attributes
    const variableTag = `<span class="editor-variable" contenteditable="false" data-variable-key="${varKey}" data-variable-id="${variableId || ''}">${displayText}</span>`;
    
    console.log('[insertVariable] Inserting:', variableTag);
    
    if (this.editorInstance && typeof this.editorInstance.selection !== 'undefined') {
      // Jodit API: usa selection.insertHTML per inserire HTML
      this.editorInstance.selection.insertHTML(variableTag);
      this.hasUnsavedChanges = true;
      
      // Ri-inietta gli stili per sicurezza
      setTimeout(() => {
        this.injectEditorStyles();
        console.log('[insertVariable] Editor content after insert:', this.editorInstance.value);
      }, 100);
    }
  }

  insertAttachment(attachmentId: number) {
    // Find the attachment to get its title
    const attachment = this.attachments.find((a) => a.id === attachmentId);
    const displayText = attachment?.title || `Allegato ${attachmentId}`;

    // Create span with title visible, but store id in data attribute
    const attachmentTag = `<span class="editor-attachment" contenteditable="false" data-attachment-id="${attachmentId}">${displayText}</span>`;
    
    if (this.editorInstance && typeof this.editorInstance.selection !== 'undefined') {
      // Jodit API: usa selection.insertHTML per inserire HTML
      this.editorInstance.selection.insertHTML(attachmentTag);
      this.hasUnsavedChanges = true;
      
      // Ri-inietta gli stili per sicurezza
      setTimeout(() => this.injectEditorStyles(), 50);
    }
  }

  setupAutocomplete(editor: any) {
    // TODO: Riscrivere per Jodit usando editor.events.on('keydown') e selezione Jodit
    // API Jodit differisce da TinyMCE: usare editor.selection invece di editor.selection.getRng()
    // Riferimento: https://xdsoft.net/jodit/docs/classes/jodit.Jodit.html
    
    let autocompleteActive = false;
    let autocompleteMenu: HTMLElement | null = null;
    let selectedIndex = 0;
    let filteredVariables: typeof this.variables = [];
    let searchQuery = '';

    const createAutocompleteMenu = (x: number, y: number, query: string = '') => {
      searchQuery = query;

      // Filter variables based on query
      filteredVariables = this.variables.filter(
        (v) =>
          query === '' ||
          v.variableKey.toLowerCase().includes(query.toLowerCase()) ||
          v.displayName.toLowerCase().includes(query.toLowerCase()),
      );

      // If no matches, close autocomplete
      if (filteredVariables.length === 0) {
        closeAutocomplete();
        return;
      }

      // Remove existing menu
      if (autocompleteMenu) {
        autocompleteMenu.remove();
      }

      // Create menu element
      autocompleteMenu = document.createElement('div');
      autocompleteMenu.className = 'tinymce-autocomplete-menu';
      autocompleteMenu.style.cssText = `
        position: fixed;
        left: ${x}px;
        top: ${y}px;
        background: white;
        border: 1px solid #ccc;
        border-radius: 4px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        min-width: 200px;
        max-height: 250px;
        overflow-y: auto;
        pointer-events: none;
      `;

      // Make it completely non-focusable
      autocompleteMenu.setAttribute('aria-hidden', 'true');

      // Make it completely non-focusable
      autocompleteMenu.setAttribute('aria-hidden', 'true');

      filteredVariables.forEach((variable, index) => {
        const item = document.createElement('div');
        item.className = 'autocomplete-item';
        item.style.cssText = `
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.2s;
          display: flex;
          flex-direction: column;
          gap: 2px;
          pointer-events: auto;
        `;

        // Main text: variableKey
        const mainText = document.createElement('div');
        mainText.style.cssText = 'font-family: monospace; font-weight: 600; color: #1976d2;';
        const variableText = variable.variableKey;

        // Highlight matching text in the key
        if (query) {
          const regex = new RegExp(`(${query})`, 'gi');
          const highlighted = variableText.replace(
            regex,
            '<span style="background: #ffd54f;">$1</span>',
          );
          mainText.innerHTML = highlighted;
        } else {
          mainText.textContent = variableText;
        }

        // Subtitle: displayName
        const subtitle = document.createElement('div');
        subtitle.style.cssText = 'font-size: 12px; color: #666; font-style: italic;';
        subtitle.textContent = variable.displayName;

        item.appendChild(mainText);
        item.appendChild(subtitle);

        item.dataset['index'] = index.toString();
        item.dataset['key'] = variable.variableKey;

        item.addEventListener('mouseenter', () => {
          selectedIndex = index;
          updateSelection();
        });

        item.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          insertSelectedVariable();
          return false;
        });

        autocompleteMenu!.appendChild(item);
      });

      document.body.appendChild(autocompleteMenu);
      autocompleteActive = true;
      selectedIndex = 0;
      updateSelection();
    };

    const updateSelection = () => {
      if (!autocompleteMenu) return;
      const items = autocompleteMenu.querySelectorAll('.autocomplete-item');
      items.forEach((item, index) => {
        const htmlItem = item as HTMLElement;
        if (index === selectedIndex) {
          htmlItem.style.background = '#e3f2fd';
          htmlItem.style.color = '#1976d2';
        } else {
          htmlItem.style.background = '';
          htmlItem.style.color = '';
        }
      });
    };

    const closeAutocomplete = () => {
      if (autocompleteMenu) {
        autocompleteMenu.remove();
        autocompleteMenu = null;
      }
      autocompleteActive = false;
    };

    const insertSelectedVariable = () => {
      const variable = filteredVariables[selectedIndex];
      if (variable) {
        // Get current node and clean text
        const rng = editor.selection.getRng();
        const node = rng.startContainer;
        const text = node.textContent || '';
        const cleanText = text.replace(/\uFEFF/g, '');

        // Find {{ before cursor
        const lastIndex = cleanText.lastIndexOf('{{');

        if (lastIndex !== -1) {
          // Calculate how many invisible chars are before lastIndex
          const textBeforeMarker = text.substring(0, lastIndex);
          const invisibleChars = (textBeforeMarker.match(/\uFEFF/g) || []).length;
          const actualLastIndex = lastIndex + invisibleChars;

          // Get actual cursor position in the original text (with invisible chars)
          const cursorPos = rng.startOffset;

          // Remove {{ and everything after it up to cursor
          const range = editor.dom.createRng();
          const textNode = node.nodeType === 3 ? node : node.firstChild || node;
          range.setStart(textNode, actualLastIndex);
          range.setEnd(textNode, cursorPos);
          editor.selection.setRng(range);
          editor.selection.setContent('');
        }

        // Insert formatted variable
        this.insertVariable(variable.variableKey, variable.id);
      }
      closeAutocomplete();
    };

    // Listen for keyup to detect {{
    editor.on('input', (e: any) => {
      // Check if {{ was typed
      const rng = editor.selection.getRng();
      const node = rng.startContainer;

      // Get text node content
      const text = node.textContent || '';
      const cursorPos = rng.startOffset;

      // Clean up any zero-width characters from markers
      const cleanText = text.replace(/\uFEFF/g, '');
      const adjustedPos = cursorPos - (text.substring(0, cursorPos).split('\uFEFF').length - 1);

      console.log(
        'Input - cleanText:',
        cleanText,
        'adjustedPos:',
        adjustedPos,
        'last2chars:',
        cleanText.substring(Math.max(0, adjustedPos - 2), adjustedPos),
      );

      // Find {{ before cursor
      const textBeforeCursor = cleanText.substring(0, adjustedPos);
      const lastBracketIndex = textBeforeCursor.lastIndexOf('{{');

      // If {{ found before cursor
      if (lastBracketIndex !== -1 && !autocompleteActive) {
        console.log('Detected {{ - opening autocomplete');

        // Get cursor position on screen
        const rect = rng.getBoundingClientRect();
        const iframe = editor.getContentAreaContainer().querySelector('iframe');
        const iframeRect = iframe ? iframe.getBoundingClientRect() : { left: 0, top: 0 };

        const absoluteX = iframeRect.left + rect.left;
        const absoluteY = iframeRect.top + rect.bottom;

        console.log('Opening menu at:', absoluteX, absoluteY);

        // Extract query after {{
        const query = textBeforeCursor.substring(lastBracketIndex + 2);
        console.log('Initial query:', query);

        // Position menu below cursor
        createAutocompleteMenu(absoluteX, absoluteY + 5, query);
      } else if (autocompleteActive && lastBracketIndex !== -1) {
        // Update filter if autocomplete is active
        const query = textBeforeCursor.substring(lastBracketIndex + 2);
        console.log('Updating filter with query:', query);

        // Get cursor position on screen
        const rect = rng.getBoundingClientRect();
        const iframe = editor.getContentAreaContainer().querySelector('iframe');
        const iframeRect = iframe ? iframe.getBoundingClientRect() : { left: 0, top: 0 };

        const absoluteX = iframeRect.left + rect.left;
        const absoluteY = iframeRect.top + rect.bottom;

        createAutocompleteMenu(absoluteX, absoluteY + 5, query);
      } else if (autocompleteActive && lastBracketIndex === -1) {
        console.log('No {{ found - closing autocomplete');
        closeAutocomplete();
      }
    });

    // Handle navigation with keydown
    editor.on(
      'keydown',
      (e: KeyboardEvent) => {
        if (autocompleteActive) {
          // Handle ONLY navigation keys
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            selectedIndex = Math.min(selectedIndex + 1, filteredVariables.length - 1);
            updateSelection();
            return false;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            selectedIndex = Math.max(selectedIndex - 1, 0);
            updateSelection();
            return false;
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            // Insert variable BEFORE closing to prevent any other handlers
            insertSelectedVariable();
            return false;
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            closeAutocomplete();
            return false;
          }

          // For all other keys (typing), let them through normally
          // The 'input' event will handle updating the filter
        }
        return true;
      },
      true,
    ); // Use capture phase to intercept before TinyMCE

    // Close on click outside
    editor.on('click', (e: MouseEvent) => {
      if (autocompleteActive) {
        // Check if click is outside the autocomplete menu
        const clickedElement = e.target as HTMLElement;
        if (!autocompleteMenu?.contains(clickedElement)) {
          closeAutocomplete();
        }
      }
    });

    // Close on blur
    editor.on('blur', () => {
      setTimeout(() => closeAutocomplete(), 200);
    });

    // Click handler for document to close menu when clicking outside editor
    const documentClickHandler = (e: MouseEvent) => {
      if (autocompleteActive && autocompleteMenu) {
        const clickedElement = e.target as HTMLElement;
        if (!autocompleteMenu.contains(clickedElement)) {
          closeAutocomplete();
        }
      }
    };
    document.addEventListener('click', documentClickHandler);

    // Cleanup on editor destroy
    editor.on('remove', () => {
      document.removeEventListener('click', documentClickHandler);
      closeAutocomplete();
    });
  }

  setupAttachmentAutocomplete(editor: any) {
    // TODO: Riscrivere per Jodit usando editor.events.on('keydown') e selezione Jodit
    // Simile a setupAutocomplete ma per trigger [[ invece di {{
    
    let autocompleteActive = false;
    let autocompleteMenu: HTMLElement | null = null;
    let selectedIndex = 0;
    let filteredAttachments: typeof this.attachments = [];
    let searchQuery = '';

    const createAutocompleteMenu = (x: number, y: number, query: string = '') => {
      searchQuery = query;

      // Filter attachments based on query
      filteredAttachments = this.attachments.filter(
        (a) =>
          query === '' ||
          a.title.toLowerCase().includes(query.toLowerCase()) ||
          (a.description && a.description.toLowerCase().includes(query.toLowerCase())),
      );

      // If no matches, close autocomplete
      if (filteredAttachments.length === 0) {
        closeAutocomplete();
        return;
      }

      // Remove existing menu
      if (autocompleteMenu) {
        autocompleteMenu.remove();
      }

      // Create menu element
      autocompleteMenu = document.createElement('div');
      autocompleteMenu.className = 'tinymce-autocomplete-menu attachment-menu';
      autocompleteMenu.style.cssText = `
        position: fixed;
        left: ${x}px;
        top: ${y}px;
        background: white;
        border: 1px solid #ffa500;
        border-radius: 4px;
        box-shadow: 0 4px 12px rgba(255,107,107,0.25);
        z-index: 10000;
        min-width: 200px;
        max-height: 250px;
        overflow-y: auto;
        pointer-events: none;
      `;

      autocompleteMenu.setAttribute('aria-hidden', 'true');

      filteredAttachments.forEach((attachment, index) => {
        const item = document.createElement('div');
        item.className = 'autocomplete-item';
        item.style.cssText = `
          padding: 8px 12px;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.2s;
          display: flex;
          flex-direction: column;
          gap: 2px;
        `;

        const title = document.createElement('strong');
        title.textContent = attachment.title;
        title.style.color = '#ff6b6b';
        item.appendChild(title);

        if (attachment.description) {
          const desc = document.createElement('div');
          desc.textContent = attachment.description;
          desc.style.cssText = 'font-size: 12px; color: #666;';
          item.appendChild(desc);
        }

        autocompleteMenu!.appendChild(item);
      });

      selectedIndex = 0;
      updateSelection();

      document.body.appendChild(autocompleteMenu);
      autocompleteActive = true;
    };

    const updateSelection = () => {
      if (!autocompleteMenu) return;

      const items = autocompleteMenu.querySelectorAll('.autocomplete-item');
      items.forEach((item, index) => {
        const htmlItem = item as HTMLElement;
        if (index === selectedIndex) {
          htmlItem.style.background = '#fff3e0';
          htmlItem.querySelectorAll('strong')[0].style.color = '#ff6b6b';
        } else {
          htmlItem.style.background = '';
          htmlItem.querySelectorAll('strong')[0].style.color = '#ff6b6b';
        }
      });
    };

    const closeAutocomplete = () => {
      if (autocompleteMenu) {
        autocompleteMenu.remove();
        autocompleteMenu = null;
      }
      autocompleteActive = false;
    };

    const insertSelectedAttachment = () => {
      const attachment = filteredAttachments[selectedIndex];
      if (attachment) {
        const rng = editor.selection.getRng();
        const node = rng.startContainer;
        const text = node.textContent || '';
        const cleanText = text.replace(/\uFEFF/g, '');

        // Find [[ before cursor
        const lastIndex = cleanText.lastIndexOf('[[');

        if (lastIndex !== -1) {
          const textBeforeMarker = text.substring(0, lastIndex);
          const invisibleChars = (textBeforeMarker.match(/\uFEFF/g) || []).length;
          const actualLastIndex = lastIndex + invisibleChars;

          const cursorPos = rng.startOffset;

          // Remove [[ and everything after it up to cursor
          const range = editor.dom.createRng();
          const textNode = node.nodeType === 3 ? node : node.firstChild || node;
          range.setStart(textNode, actualLastIndex);
          range.setEnd(textNode, cursorPos);
          editor.selection.setRng(range);
          editor.selection.setContent('');
        }

        // Insert attachment
        this.insertAttachment(attachment.id!);
        closeAutocomplete();
      }
    };

    editor.on('input', (e: any) => {
      const rng = editor.selection.getRng();
      const node = rng.startContainer;

      const text = node.textContent || '';
      const cursorPos = rng.startOffset;

      const cleanText = text.replace(/\uFEFF/g, '');
      const adjustedPos = cursorPos - (text.substring(0, cursorPos).split('\uFEFF').length - 1);

      // Find [[ before cursor
      const textBeforeCursor = cleanText.substring(0, adjustedPos);
      const lastBracketIndex = textBeforeCursor.lastIndexOf('[[');

      // If [[ found before cursor
      if (lastBracketIndex !== -1 && !autocompleteActive) {
        const rect = rng.getBoundingClientRect();
        const iframe = editor.getContentAreaContainer().querySelector('iframe');
        const iframeRect = iframe ? iframe.getBoundingClientRect() : { left: 0, top: 0 };

        const absoluteX = iframeRect.left + rect.left;
        const absoluteY = iframeRect.top + rect.bottom;

        const query = textBeforeCursor.substring(lastBracketIndex + 2);
        createAutocompleteMenu(absoluteX, absoluteY + 5, query);
      } else if (autocompleteActive && lastBracketIndex !== -1) {
        const query = textBeforeCursor.substring(lastBracketIndex + 2);

        const rect = rng.getBoundingClientRect();
        const iframe = editor.getContentAreaContainer().querySelector('iframe');
        const iframeRect = iframe ? iframe.getBoundingClientRect() : { left: 0, top: 0 };

        const absoluteX = iframeRect.left + rect.left;
        const absoluteY = iframeRect.top + rect.bottom;

        createAutocompleteMenu(absoluteX, absoluteY + 5, query);
      } else if (autocompleteActive && lastBracketIndex === -1) {
        closeAutocomplete();
      }
    });

    editor.on(
      'keydown',
      (e: KeyboardEvent) => {
        if (autocompleteActive) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            selectedIndex = Math.min(selectedIndex + 1, filteredAttachments.length - 1);
            updateSelection();
            return false;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            selectedIndex = Math.max(selectedIndex - 1, 0);
            updateSelection();
            return false;
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            insertSelectedAttachment();
            return false;
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            closeAutocomplete();
            return false;
          }
        }
        return true;
      },
      true,
    );

    editor.on('click', (e: MouseEvent) => {
      if (autocompleteActive) {
        const clickedElement = e.target as HTMLElement;
        if (!autocompleteMenu?.contains(clickedElement)) {
          closeAutocomplete();
        }
      }
    });

    editor.on('blur', () => {
      setTimeout(() => closeAutocomplete(), 200);
    });

    const documentClickHandler = (e: MouseEvent) => {
      if (autocompleteActive && autocompleteMenu) {
        const clickedElement = e.target as HTMLElement;
        if (!autocompleteMenu.contains(clickedElement)) {
          closeAutocomplete();
        }
      }
    };
    document.addEventListener('click', documentClickHandler);

    editor.on('remove', () => {
      document.removeEventListener('click', documentClickHandler);
      closeAutocomplete();
    });
  }

  /**
   * Extract variable IDs from editor content by parsing data-variable-id attributes
   * Returns unique IDs only (no duplicates even if variable used multiple times)
   */
  private extractVariableIds(content: string): number[] {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const variableSpans = tempDiv.querySelectorAll('span.editor-variable[data-variable-id]');
    const idsSet = new Set<number>();

    variableSpans.forEach((span: Element) => {
      const idAttr = span.getAttribute('data-variable-id');
      if (idAttr && idAttr !== '') {
        const id = parseInt(idAttr, 10);
        if (!isNaN(id)) {
          idsSet.add(id);
        }
      }
    });

    return Array.from(idsSet);
  }

  /**
   * Convert editor HTML (with spans) to database format {{variableKey.id}}
   */
  private convertSpansToVariableFormat(content: string): string {
    console.log('[convertSpansToVariableFormat] Input content:', content);
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const variableSpans = tempDiv.querySelectorAll('span.editor-variable');
    console.log('[convertSpansToVariableFormat] Found spans:', variableSpans.length);

    variableSpans.forEach((span: Element, index) => {
      const varKey = span.getAttribute('data-variable-key');
      const varId = span.getAttribute('data-variable-id');
      console.log(`[convertSpansToVariableFormat] Span ${index}:`, { varKey, varId, html: span.outerHTML });

      if (varKey && varId) {
        // Remove {{ and }} from varKey, then add .id and }}
        const cleanKey = varKey.replace(/^\{\{/, '').replace(/\}\}$/, '');
        const variableText = `{{${cleanKey}.${varId}}}`;
        console.log(`[convertSpansToVariableFormat] Converting to:`, variableText);

        // Capture parent before replacing
        const parentEl = span.parentElement;

        const textNode = document.createTextNode(variableText);
        span.replaceWith(textNode);

        // FIX: Strip font-size-only wrapper span added by the editor at insertion time.
        // When a variable is inserted, Jodit may wrap it in a <span style="font-size:Xpx">
        // capturing the current cursor's size. This prevents the value from inheriting
        // the correct size when the surrounding text is later reformatted.
        if (
          parentEl &&
          parentEl !== tempDiv &&
          parentEl.tagName === 'SPAN' &&
          !parentEl.className &&
          parentEl.style.fontSize &&
          !parentEl.style.fontWeight &&
          !parentEl.style.fontStyle &&
          !parentEl.style.color &&
          !parentEl.style.textDecoration &&
          !parentEl.style.textAlign &&
          parentEl.childNodes.length === 1
        ) {
          parentEl.style.removeProperty('font-size');
          if (parentEl.style.length === 0 && parentEl.attributes.length === 0) {
            const fragment = document.createDocumentFragment();
            while (parentEl.firstChild) fragment.appendChild(parentEl.firstChild);
            parentEl.replaceWith(fragment);
          }
        }
      }
    });

    const result = tempDiv.innerHTML;
    console.log('[convertSpansToVariableFormat] Output content:', result);
    return result;
  }

  /**
   * Convert database format {{variableKey.id}} to editor HTML (with spans showing displayName)
   */
  private convertVariableFormatToSpans(content: string): string {
    // Pattern matches: {{varKey.number}} but NOT {{attachment.number}}
    // The negative lookahead (?!attachment\.) excludes attachment patterns
    const variablePattern = /\{\{(?!attachment\.)([^.}]+)\.(\d+)\}\}/g;

    return content.replace(variablePattern, (match, varKeyPart, varId) => {
      const id = parseInt(varId, 10);
      const variable = this.variables.find((v) => v.id === id);
      const displayText = variable?.displayName || match;
      const fullVarKey = `{{${varKeyPart}}}`;

      return `<span class="editor-variable" contenteditable="false" data-variable-key="${fullVarKey}" data-variable-id="${varId}">${displayText}</span>`;
    });
  }

  /**
   * Extract attachment IDs from content
   */
  private extractAttachmentIds(content: string): number[] {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const attachmentSpans = tempDiv.querySelectorAll('span.editor-attachment[data-attachment-id]');
    const idsSet = new Set<number>();

    attachmentSpans.forEach((span: Element) => {
      const idAttr = span.getAttribute('data-attachment-id');
      if (idAttr && idAttr !== '') {
        const id = parseInt(idAttr, 10);
        if (!isNaN(id)) {
          idsSet.add(id);
        }
      }
    });

    return Array.from(idsSet);
  }

  /**
   * Convert editor HTML (with spans) to database format {{attachment.id}}
   */
  private convertSpansToAttachmentFormat(content: string): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const attachmentSpans = tempDiv.querySelectorAll('span.editor-attachment');

    attachmentSpans.forEach((span: Element) => {
      const attachmentId = span.getAttribute('data-attachment-id');

      if (attachmentId) {
        const attachmentText = `{{attachment.${attachmentId}}}`;

        const textNode = document.createTextNode(attachmentText);
        span.replaceWith(textNode);
      }
    });

    return tempDiv.innerHTML;
  }

  /**
   * Convert database format {{attachment.id}} to editor HTML (with spans showing title)
   */
  private convertAttachmentFormatToSpans(content: string): string {
    // Pattern matches: {{attachment.number}}
    const attachmentPattern = /\{\{attachment\.(\d+)\}\}/g;

    return content.replace(attachmentPattern, (match, attachmentId) => {
      const id = parseInt(attachmentId, 10);
      const attachment = this.attachments.find((a) => a.id === id);
      const displayText = attachment?.title || `Allegato ${attachmentId}`;

      return `<span class="editor-attachment" contenteditable="false" data-attachment-id="${attachmentId}">${displayText}</span>`;
    });
  }

  saveSection() {
    this.hasAttemptedSave = true;

    if (!this.name.trim() || !this.contentText) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Compilare nome e contenuto del blocco di testo',
        life: 3000,
      });
      return;
    }

    if (!this.rootTable) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: "Selezionare l'entità  principale del blocco di testo",
        life: 3000,
      });
      return;
    }

    // Convert spans to {{varKey.id}} and {{attachment.id}} format for database
    console.log('[saveSection] Original contentText:', this.contentText);
    let contentForDatabase = this.convertSpansToVariableFormat(this.contentText);
    console.log('[saveSection] After convertSpansToVariableFormat:', contentForDatabase);
    contentForDatabase = this.convertSpansToAttachmentFormat(contentForDatabase);
    console.log('[saveSection] After convertSpansToAttachmentFormat:', contentForDatabase);

    // Extract variable IDs and attachment IDs from content
    this.variableIds = this.extractVariableIds(this.contentText);
    this.attachmentIds = this.extractAttachmentIds(this.contentText);
    console.log('[Save Section] Extracted variable IDs:', [...this.variableIds]);
    console.log('[Save Section] Extracted attachment IDs:', [...this.attachmentIds]);

    const sectionRequest: CreateSectionRequest = {
      name: this.name,
      contentText: contentForDatabase,
      variableIds: this.variableIds,
      attachmentIds: this.attachmentIds,
      rootTable: this.rootTable,
      isRepeatable: this.isRepeatable,
    };

    const saveObs = this.editId
      ? this.sectionRegistryService.updateSectionPut(this.editId, sectionRequest)
      : this.sectionRegistryService.createSection(sectionRequest);

    this.isSaving = true;
    saveObs.subscribe({
      next: (response) => {
        if (response?.status) {
          const wasCreating = !this.editId;
          
          this.messageService.add({
            severity: 'success',
            summary: 'Successo',
            detail: this.editId
              ? 'Blocco di testo aggiornato con successo'
              : 'Blocco di testo creato con successo',
            life: 3000,
          });
          
          // Se era una POST (creazione o clone), aggiorna editId e cambia route a edit mode
          if (wasCreating && response.data) {
            let newId: number | null = null;
            
            // Estrai l'ID dalla response (gestisci vari formati)
            const data: any = response.data;
            
            if (typeof data === 'number') {
              newId = data;
            } else if (data.id) {
              newId = data.id;
            } else if (Array.isArray(data) && data.length > 0 && data[0].id) {
              newId = data[0].id;
            } else if (data.content && typeof data.content === 'object' && data.content.id) {
              newId = data.content.id;
            }
            
            if (newId) {
              // Aggiorna l'editId per le prossime save (diventeranno PUT)
              this.editId = newId;
              console.log('[saveSection] POST successful, now in EDIT mode with ID:', newId);
              
              // Cambia la route da /new a /edit/:id senza ricaricare la pagina
              this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contract-sections/edit`, newId], {
                replaceUrl: true // Sostituisce l'entry nella history invece di aggiungerne una nuova
              });
            }
          }
          
          // Resetta il flag unsaved changes (abbiamo appena salvato)
          this.hasUnsavedChanges = false;
          
          // NON resettiamo il form, rimaniamo sulla stessa pagina con gli stessi dati
        }
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error saving section:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile salvare il blocco di testo',
          life: 5000,
        });
        this.isSaving = false;
      },
    });
  }

  editSection(section: Section) {
    // Pulisci overlay prima di qualsiasi azione
    this.clearActiveOverlays();
    
    // Check for unsaved changes
    if (this.hasUnsavedChanges) {
      this.confirmDialogService.confirmCancelEdit(() => {
        this.loadSectionDetails(section);
      });
      return;
    }

    this.loadSectionDetails(section);
  }

  private loadSectionDetails(section: Section) {
    this.clearActiveOverlays();
    
    if (!section.id) {
      // Fallback: use data from the list if no ID
      setTimeout(() => {
        this.editId = null;
        this.name = section.name;
        this.contentText = section.contentText || '';
        this.variableIds =
          section.variables?.map((v) => v.id!).filter((id) => id !== undefined) ||
          section.variableIds ||
          [];
        this.attachmentIds =
          section.attachments?.map((a) => a.id!).filter((id) => id !== undefined) ||
          section.attachmentIds ||
          [];
        this.rootTable = section.rootTable || null;
        this.isRepeatable = section.isRepeatable || false;
        this.hasUnsavedChanges = false;

        // XSS: sanitize raw content ({{var.id}} format) BEFORE converting to spans,
        // so DOMPurify does not strip data-* attributes from the generated spans
        this.contentText = this.htmlSanitizer.sanitize(this.contentText);
        // Convert {{varKey.id}} and {{attachment.id}} format to spans with displayName/title
        this.contentText = this.convertVariableFormatToSpans(this.contentText);
        this.contentText = this.convertAttachmentFormatToSpans(this.contentText);

        this.setEditorContent(this.contentText);
      }, 100);
      return;
    }

    // Load full section details from API to get complete data including variableIds
    this.sectionRegistryService.getSection(section.id).subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          let fullSection: Section;

          if (Array.isArray(response.data)) {
            fullSection = response.data[0];
          } else if ('content' in response.data) {
            // It's a paginated response
            fullSection = response.data.content[0];
          } else {
            fullSection = response.data;
          }

          // Enrich attachments array with attachments from this section
          this.enrichAttachmentsFromSections([fullSection]);

          // Wrap assignments in setTimeout to avoid NG0100
          setTimeout(() => {
            this.editId = fullSection.id || null;
            this.name = fullSection.name;
            this.contentText = fullSection.contentText || '';
            this.variableIds =
              fullSection.variables?.map((v) => v.id!).filter((id) => id !== undefined) ||
              fullSection.variableIds ||
              [];
            this.attachmentIds =
              fullSection.attachments?.map((a) => a.id!).filter((id) => id !== undefined) ||
              fullSection.attachmentIds ||
              [];
            this.rootTable = fullSection.rootTable || null;
            this.isRepeatable = fullSection.isRepeatable || false;
            this.hasUnsavedChanges = false;

            // XSS: sanitize raw content ({{var.id}} format) BEFORE converting to spans,
            // so DOMPurify does not strip data-* attributes from the generated spans
            this.contentText = this.htmlSanitizer.sanitize(this.contentText);
            // Convert {{varKey.id}} and {{attachment.id}} format to spans with displayName/title
            this.contentText = this.convertVariableFormatToSpans(this.contentText);
            this.contentText = this.convertAttachmentFormatToSpans(this.contentText);

            this.setEditorContent(this.contentText);
          }, 100);
        }
      },
      error: (error) => {
        console.error('Error loading section details:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i dettagli del blocco di testo',
          life: 5000,
        });
      },
    });
  }

  /**
   * Carica e apre una sezione specifica in modifica (usato quando si naviga dal template editor)
   */
  loadAndEditSection(sectionId: number) {
    // Cerca la sezione nell'array già  caricato
    const section = this.sections.find((s) => s.id === sectionId);

    if (section) {
      // Sezione trovata, aprila in modifica
      this.editSection(section);
    } else {
      // Sezione non trovata nell'elenco, caricala direttamente dall'API
      this.sectionRegistryService.getSection(sectionId).subscribe({
        next: (response) => {
          if (response?.status && response?.data) {
            let fullSection: Section;

            if (Array.isArray(response.data)) {
              fullSection = response.data[0];
            } else if ('content' in response.data) {
              fullSection = response.data.content[0];
            } else {
              fullSection = response.data;
            }

            // Enrich attachments array with attachments from this section
            this.enrichAttachmentsFromSections([fullSection]);

            // Apri la sezione in modifica
            this.editSection(fullSection);
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: 'Attenzione',
              detail: 'Blocco di testo non trovato',
              life: 3000,
            });
          }
        },
        error: (error) => {
          console.error('Error loading section:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore',
            detail: 'Impossibile caricare il blocco di testo',
            life: 5000,
          });
        },
      });
    }
  }

  deleteSection(section: Section) {
    if (!section.id) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Impossibile eliminare un blocco di testo senza ID',
        life: 3000,
      });
      return;
    }

    this.confirmDialogService.confirmDelete(
      'Sei sicuro di voler eliminare questo blocco di testo? Questa azione non può essere annullata.',
      () => {
        this.sectionRegistryService.deleteSection(section.id!).subscribe({
          next: (response) => {
            if (response?.status) {
              this.messageService.add({
                severity: 'success',
                summary: 'Successo',
                detail: 'Blocco di testo eliminato con successo',
                life: 3000,
              });
              this.loadSections();
            }
          },
          error: (error) => {
            console.error('Error deleting section:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Impossibile eliminare il blocco di testo',
              life: 5000,
            });
          },
        });
      },
    );
  }

  cloneSection(section: Section) {
    // Pulisci overlay prima di qualsiasi azione
    this.clearActiveOverlays();
    
    // Check for unsaved changes
    if (this.hasUnsavedChanges) {
      this.confirmDialogService.confirmCancelEdit(() => {
        this.prepareCloneSection(section);
      });
      return;
    }

    this.prepareCloneSection(section);
  }

  /**
   * Carica una sezione dal backend per clonarla (route-based clone).
   * Utilizzato quando si naviga a /axa/contract-sections/new?cloneFrom=:id
   */
  private loadSectionForClone(sectionId: number): void {
    this.sectionRegistryService.getSections().subscribe({
      next: (response) => {
        if (response?.status && response?.data) {
          let dataArray: Section[] = [];

          // Check if data has content property (paginated response)
          if ('content' in response.data) {
            dataArray = Array.isArray(response.data.content)
              ? response.data.content
              : [response.data.content];
          } else if (Array.isArray(response.data)) {
            dataArray = response.data;
          } else {
            dataArray = [response.data];
          }

          // Trova la sezione con l'ID specificato
          const section = dataArray.find((s: Section) => s.id === sectionId);
          
          if (section) {
            // Arricchisci i riferimenti degli allegati
            this.enrichAttachmentsFromSections([section]);
            // Prepara la sezione per la clonazione (editId=null, nome prefissato)
            this.prepareCloneSection(section);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Blocco di testo non trovato',
              life: 5000,
            });
            this.resetEditor();
          }
        } else {
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Formato dati non valido ricevuto dal server',
            life: 4000,
          });
          this.resetEditor();
        }
      },
      error: (error) => {
        console.error('Error loading section for clone:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare il blocco da clonare',
          life: 5000,
        });
        this.resetEditor();
      },
    });
  }
  
  private prepareCloneSection(section: Section) {
    this.editId = null;
    this.name = `Copia di ${section.name}`;
    this.contentText = section.contentText || '';
    this.variableIds =
      section.variables?.map((v) => v.id!).filter((id) => id !== undefined) ||
      section.variableIds ||
      [];
    this.attachmentIds =
      section.attachments?.map((a) => a.id!).filter((id) => id !== undefined) ||
      section.attachmentIds ||
      [];
    this.rootTable = section.rootTable || null;
    this.isRepeatable = section.isRepeatable || false;
    this.hasUnsavedChanges = false;

    // XSS: sanitize raw content ({{var.id}} format) BEFORE converting to spans,
    // so DOMPurify does not strip data-* attributes from the generated spans
    this.contentText = this.htmlSanitizer.sanitize(this.contentText);
    // Convert {{varKey.id}} and {{attachment.id}} format to spans with displayName/title
    this.contentText = this.convertVariableFormatToSpans(this.contentText);
    this.contentText = this.convertAttachmentFormatToSpans(this.contentText);

    this.setEditorContent(this.contentText);
  }

  newSection() {
    // Pulisci overlay prima di qualsiasi azione
    this.clearActiveOverlays();
    
    // Check for unsaved changes
    if (this.hasUnsavedChanges) {
      this.confirmDialogService.confirmCancelEdit(() => {
        this.resetEditor();
      });
      return;
    }

    this.resetEditor();
  }

  resetEditor() {
    this.editId = null;
    this.name = '';
    this.contentText = '';
    this.variableIds = [];
    this.attachmentIds = [];
    this.rootTable = null;
    this.isRepeatable = false;
    this.hasUnsavedChanges = false;
    this.hasAttemptedSave = false;
    this.titleTouched = false;
    this.rootTableTouched = false;
    console.log('[Variables Reset] Complete list:', [...this.variableIds]);
    console.log('[Attachments Reset] Complete list:', [...this.attachmentIds]);

    // Clear Jodit editor content
    if (this.editorInstance) {
      this.editorInstance.value = '';
    }
  }

  filterSections() {
    // Ottieni il contesto corrente (SUPPLIERS o PROFESSIONALS)
    const currentDomainType = this.navigationContext.getCurrentContext().domainType;
    
    // Prima filtra per domainType
    let sectionsToFilter = this.sections.filter((section) => {
      // Se la sezione ha un domainType, deve corrispondere al contesto corrente
      if (section.domainType && section.domainType !== currentDomainType) {
        return false;
      }
      return true;
    });
    
    // Poi filtra per testo di ricerca
    const search = this.searchText.toLowerCase().trim();
    if (!search) {
      this.filteredSections = sectionsToFilter;
    } else {
      this.filteredSections = sectionsToFilter.filter((section) =>
        section.name.toLowerCase().includes(search),
      );
    }
  }

  cancelEdit() {
    this.resetEditor();
  }

  /**
   * Convert database format {{variableKey.id}} to preview HTML with displayName and styles
   */
  private formatVariablesForPreview(content: string): string {
    // Pattern matches: {{varKey.number}}
    const variablePattern = /\{\{([^.}]+)\.(\d+)\}\}/g;

    return content.replace(variablePattern, (match, varKeyPart, varId) => {
      const id = parseInt(varId, 10);
      const variable = this.variables.find((v) => v.id === id);
      const displayText = variable?.displayName || match;

      // Use exact same inline styles as TinyMCE editor content_style
      return `<span class="editor-variable" style="display: inline-block; padding: 2px 8px; margin: 0 2px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; font-weight: 600; font-style: italic; border-radius: 4px; cursor: default; user-select: none; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);">${displayText}</span>`;
    });
  }

  private formatAttachmentsForPreview(content: string): string {
    // Pattern matches: {{attachment.number}}
    const attachmentPattern = /\{\{attachment\.(\d+)\}\}/g;

    return content.replace(attachmentPattern, (match, attachmentId) => {
      const id = parseInt(attachmentId, 10);
      const attachment = this.attachments.find((a) => a.id === id);
      const displayText = attachment?.title || `Allegato ${attachmentId}`;

      // Use orange gradient for attachments
      return `<span class="editor-attachment" style="display: inline-block; padding: 2px 8px; margin: 0 2px; background: linear-gradient(135deg, #ff6b6b 0%, #ffa500 100%); color: white; font-weight: 600; font-style: italic; border-radius: 4px; cursor: default; user-select: none; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);">${displayText}</span>`;
    });
  }

  previewSection(section: Section) {
    // Convert {{varKey.id}} and {{attachment.id}} to styled HTML
    let formattedContent = this.formatVariablesForPreview(section.contentText || '');
    formattedContent = this.formatAttachmentsForPreview(formattedContent);

    this.previewData = {
      title: section.name,
      content: this.htmlSanitizer.sanitize(formattedContent), // XSS protection with DOMPurify
    };
    this.previewVisible = true;
  }

  previewCurrent() {
    if (!this.contentText) {
      this.messageService.add({
        severity: 'info',
        summary: 'Anteprima',
        detail: "Inserisci del contenuto per visualizzare l'anteprima",
        life: 3000,
      });
      return;
    }

    // Convert spans to {{varKey.id}} and {{attachment.id}} format first, then format for preview
    let contentWithVariables = this.convertSpansToVariableFormat(this.contentText);
    contentWithVariables = this.convertSpansToAttachmentFormat(contentWithVariables);
    let formattedContent = this.formatVariablesForPreview(contentWithVariables);
    formattedContent = this.formatAttachmentsForPreview(formattedContent);

    this.previewData = {
      title: this.name || 'Anteprima',
      content: this.htmlSanitizer.sanitize(formattedContent), // XSS protection with DOMPurify
    };
    this.previewVisible = true;
  }

  /**
   * Prepara l'HTML grezzo dell'editor per l'export Word:
   * - riempie celle vuote con &nbsp;
   * - imposta dimensioni minime e padding sulle celle
   * - normalizza i bordi nascosti/mancanti su celle e tabelle
   * - converte font-size da px a pt (html-docx-js / backend interpretano pt, non px)
   */
  private prepareHtmlForWordExport(rawHtml: string): string {
    // Jodit può serializzare gli span contenteditable="false" come testo puro {{...}}
    // se li ha persi internamente (es. dopo un reload). Riconvertiamo prima di procedere.
    let html = rawHtml;
    if (html.includes('{{')) {
      html = this.convertVariableFormatToSpans(html);
      html = this.convertAttachmentFormatToSpans(html);
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Riempie celle vuote
    tempDiv.querySelectorAll<HTMLElement>('td:empty, th:empty').forEach((cell) => {
      cell.innerHTML = '&nbsp;';
    });

    // Normalizza celle
    tempDiv.querySelectorAll<HTMLElement>('td, th').forEach((cell) => {
      cell.style.minHeight = '20px';
      cell.style.minWidth = '50px';
      cell.style.padding = '5px';

      if (
        cell.style.borderStyle === 'hidden' ||
        cell.style.borderStyle === 'none'
      ) {
        cell.style.border = 'none';
        cell.style.borderStyle = 'none';
      } else {
        // Se ha border-color ma mancano border-width o border-style, li completa
        const hasBorderColor = !!cell.style.borderColor;
        if (hasBorderColor) {
          if (!cell.style.borderWidth) cell.style.borderWidth = '1px';
          if (!cell.style.borderStyle) cell.style.borderStyle = 'solid';
        }
      }
    });

    // Normalizza tabelle
    tempDiv.querySelectorAll<HTMLElement>('table').forEach((table) => {
      if (table.style.borderStyle === 'hidden' || table.style.borderStyle === 'none') {
        table.style.border = 'none';
        table.style.borderStyle = 'none';
      } else {
        if (!table.style.borderCollapse) {
          table.style.borderCollapse = 'collapse';
        }
      }
    });

    // Applica stili inline Word-compatibili agli span placeholder.
    // Problema: Jodit spezza i tag di formattazione (strong/em) attorno agli span
    // contenteditable="false" invece di includerli. Es:
    //   <strong>testo </strong><span class="editor-variable">Var</span><strong> dopo</strong>
    // Quindi risalire il DOM con parentElement non basta: bisogna guardare anche i sibling.
    tempDiv.querySelectorAll<HTMLElement>('.editor-variable, .editor-attachment').forEach((el) => {
      const isAttachment = el.classList.contains('editor-attachment');

      // Rilevazione formattazione: controlla antenati E sibling adiacenti
      let isBold = false;
      let isItalic = false;
      let isUnderline = false;

      const boldTags = new Set(['STRONG', 'B']);
      const italicTags = new Set(['EM', 'I']);
      const underlineTags = new Set(['U']);

      // 1. Risali gli antenati (caso in cui il placeholer è DENTRO un tag di formato)
      let ancestor = el.parentElement;
      while (ancestor && ancestor !== tempDiv) {
        if (boldTags.has(ancestor.tagName)) isBold = true;
        if (italicTags.has(ancestor.tagName)) isItalic = true;
        if (underlineTags.has(ancestor.tagName)) isUnderline = true;
        if (ancestor.style.fontWeight === 'bold' || ancestor.style.fontWeight === '700' || ancestor.style.fontWeight === '600') isBold = true;
        if (ancestor.style.fontStyle === 'italic') isItalic = true;
        if (ancestor.style.textDecoration?.includes('underline')) isUnderline = true;
        ancestor = ancestor.parentElement;
      }

      // 2. Controlla i sibling adiacenti (caso in cui Jodit ha spezzato il tag attorno al placeholder)
      const checkNodeFormatting = (node: Node | null) => {
        if (!node) return;
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as HTMLElement;
          if (boldTags.has(el.tagName)) isBold = true;
          if (italicTags.has(el.tagName)) isItalic = true;
          if (underlineTags.has(el.tagName)) isUnderline = true;
          if (el.style.fontWeight === 'bold' || el.style.fontWeight === '700' || el.style.fontWeight === '600') isBold = true;
          if (el.style.fontStyle === 'italic') isItalic = true;
          if (el.style.textDecoration?.includes('underline')) isUnderline = true;
        }
      };
      // Sibling precedente (salta nodi di testo vuoti o solo &nbsp;)
      // Nota: \u00a0 NON viene rimosso da trim() in JS, va gestito esplicitamente
      const isBlankText = (n: Node) =>
        n.nodeType === Node.TEXT_NODE && !(n.textContent || '').replace(/[\s\u00a0]/g, '');
      let prev = el.previousSibling;
      while (prev && isBlankText(prev)) prev = prev.previousSibling;
      checkNodeFormatting(prev);
      // Sibling successivo
      let next = el.nextSibling;
      while (next && isBlankText(next)) next = next.nextSibling;
      checkNodeFormatting(next);

      // Unwrap parent solo-font-size aggiunto da Jodit
      const parent = el.parentElement;
      if (
        parent &&
        parent !== tempDiv &&
        parent.tagName === 'SPAN' &&
        !parent.className &&
        parent.style.fontSize &&
        !parent.style.fontWeight &&
        !parent.style.fontStyle &&
        !parent.style.color &&
        !parent.style.textDecoration &&
        parent.childNodes.length === 1
      ) {
        parent.style.removeProperty('font-size');
        if (parent.style.length === 0 && parent.attributes.length === 0) {
          parent.replaceWith(...Array.from(parent.childNodes));
        }
      }

      // Applica stili inline Word-compatibili
      const bgColor = isAttachment ? '#ff6b6b' : '#667eea';
      const fwStyle = isBold ? 'bold' : 'normal';
      const fiStyle = isItalic ? 'italic' : 'normal';
      const tdStyle = isUnderline ? 'underline' : 'none';
      el.setAttribute('style',
        `display: inline; padding: 1px 6px; background-color: ${bgColor}; color: white; ` +
        `font-weight: ${fwStyle}; font-style: ${fiStyle}; text-decoration: ${tdStyle};`
      );
    });

    // Rimuovi &nbsp; sintetico (aggiunto da Jodit) immediatamente dopo uno span placeholder
    // solo per l'export Word: il testo rimane intatto nel DB.
    tempDiv.querySelectorAll<HTMLElement>('.editor-variable, .editor-attachment').forEach((el) => {
      const next = el.nextSibling;
      if (next && next.nodeType === Node.TEXT_NODE) {
        const t = next.textContent || '';
        if (t === '\u00a0') {
          next.parentNode?.removeChild(next);
        } else if (t.startsWith('\u00a0')) {
          next.textContent = t.slice(1);
        }
      }
    });

    // Converti font-size da px a pt
    // html-docx-js e il backend Word interpretano i valori come pt:
    // senza questa conversione, 9px → 6.75pt ≈ 7pt in Word.
    let result = tempDiv.innerHTML.replace(/font-size:\s*(\d+(?:\.\d+)?)px/gi, 'font-size: $1pt');

    // Applica font/size di default solo sugli elementi che NON hanno già un valore
    // esplicito, in modo da rispettare la formattazione proveniente dal BE.
    const fixDiv = document.createElement('div');
    fixDiv.innerHTML = result;
    const DEFAULT_WORD_FONT = "'Source Sans Pro', 'Source Sans 3', 'Segoe UI', sans-serif";
    fixDiv.querySelectorAll<HTMLElement>('*').forEach((el) => {
      if (!el.style.fontFamily) el.style.fontFamily = DEFAULT_WORD_FONT;
      if (!el.style.fontSize) el.style.fontSize = '10pt';
    });
    result = fixDiv.innerHTML;

    return result;
  }

  generateWord() {
    if (!this.name || !this.contentText) {
      alert('Please add section name and content first');
      return;
    }

    const cleanHtml = this.prepareHtmlForWordExport(this.contentText);

    this.sectionsService.generateWord(this.name, cleanHtml, null).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.name}.docx`;
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Error generating Word:', error);
        alert('Error generating Word document');
      },
    });
  }

  generateWordFrontend() {
    if (!this.name || !this.contentText) {
      alert('Please add section name and content first');
      return;
    }

    this.isGenerating = true;

    setTimeout(() => {
      try {
        const cleanHtml = this.prepareHtmlForWordExport(this.contentText);
        const wrappedHtml = this.wordExportService.wrapHtmlForWord(cleanHtml);
        const rawBlob: Blob = (window as any).htmlDocx.asBlob(wrappedHtml);
        this.wordExportService.addWordFooter(rawBlob)
          .then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${this.name}_frontend.docx`;
            link.click();
            window.URL.revokeObjectURL(url);
          })
          .catch((error) => console.error('Error generating Word (frontend):', error))
          .finally(() => { this.isGenerating = false; });
      } catch (error) {
        console.error('Error generating Word (frontend):', error);
        alert('Error generating Word document from frontend');
        this.isGenerating = false;
      }
    }, 100);
  }

  generatePdfFrontend() {
    if (!this.name || !this.contentText) {
      alert('Please add section name and content first');
      return;
    }

    this.isGenerating = true;

    // Small delay to show loading indicator
    setTimeout(() => {
      // Clean HTML for PDF
      let cleanHtml = this.contentText;

      // Create a temporary div for processing
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cleanHtml;
      tempDiv.style.padding = '20px';
      tempDiv.style.fontFamily = 'Arial, sans-serif';

      // Ensure tables have proper borders and styling
      const allTables = tempDiv.querySelectorAll('table');
      allTables.forEach((table: Element) => {
        const htmlTable = table as HTMLElement;
        htmlTable.style.borderCollapse = 'collapse';
        htmlTable.style.width = '100%';
        htmlTable.style.marginBottom = '10px';
      });

      // Ensure cells have proper styling
      const allCells = tempDiv.querySelectorAll('td, th');
      allCells.forEach((cell: Element) => {
        const htmlCell = cell as HTMLElement;
        if (!htmlCell.innerHTML.trim()) {
          htmlCell.innerHTML = '&nbsp;';
        }
        htmlCell.style.padding = '8px';
        htmlCell.style.minHeight = '20px';
      });

      cleanHtml = tempDiv.innerHTML;

      // PDF generation options
      const opt = {
        margin: 10,
        filename: `${this.name}_frontend.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm' as const, format: 'a4' as const, orientation: 'portrait' as const },
      };

      // Generate PDF using html2pdf
      try {
        html2pdf()
          .set(opt)
          .from(cleanHtml)
          .save()
          .then(() => {
            this.isGenerating = false;
          })
          .catch((error: any) => {
            console.error('Error generating PDF (frontend):', error);
            alert('Error generating PDF document from frontend');
            this.isGenerating = false;
          });
      } catch (error) {
        console.error('Error generating PDF (frontend):', error);
        alert('Error generating PDF document from frontend');
        this.isGenerating = false;
      }
    }, 100);
  }

  /**
   * Rileva se l'HTML incollato proviene da Microsoft Word/Office.
   */
  private isWordHtml(html: string): boolean {
    return (
      html.includes('mso-') ||
      html.includes('MsoNormal') ||
      html.includes('urn:schemas-microsoft-com') ||
      html.includes('<o:p') ||
      html.includes('<!--[if')
    );
  }

  /**
   * Rimuove il markup proprietario Microsoft Word dall'HTML incollato,
   * preservando solo tag e stili utili per l'editor (grassetto, corsivo,
   * sottolineato, tabelle, liste, ecc.).
   */
  private stripWordHtml(html: string): string {
    // 1. Rimuove commenti condizionali Word (<!--[if ...]>...<![endif]-->)
    //    inclusi quelli che wrappano blocchi <xml> con i metadati
    let cleaned = html.replace(/<!--\[if[^\]]*\]>[\s\S]*?<!\[endif\]-->/gi, '');
    // Rimuove anche i tag condizionali incompleti rimasti
    cleaned = cleaned.replace(/<!--\[if[^\]]*\]-->/gi, '');
    cleaned = cleaned.replace(/<!--\[endif\]-->/gi, '');

    // 2. Rimuove blocchi <xml>...</xml> (metadati Word)
    cleaned = cleaned.replace(/<xml[^>]*>[\s\S]*?<\/xml>/gi, '');

    // 3. Parsing DOM per pulizia strutturale
    const parser = new DOMParser();
    const doc = parser.parseFromString(cleaned, 'text/html');

    // 4. Rimuove tag con namespace Office/Word (o:p, w:*, m:*)
    //    sostituendoli con il loro contenuto testuale
    doc.querySelectorAll('*').forEach(el => {
      const tag = el.tagName.toLowerCase();
      if (tag.startsWith('o:') || tag.startsWith('w:') || tag.startsWith('m:')) {
        el.replaceWith(...Array.from(el.childNodes));
      }
    });

    // 5. Sugli elementi rimasti: rimuove classi Word e proprietà CSS mso-*
    doc.querySelectorAll('*').forEach(el => {
      // Rimuove classi Mso* (MsoNormal, MsoBodyText, ecc.)
      if (el.classList?.length) {
        Array.from(el.classList)
          .filter(c => c.startsWith('Mso') || c.startsWith('mso'))
          .forEach(c => el.classList.remove(c));
        if (el.classList.length === 0) {
          el.removeAttribute('class');
        }
      }

      // Filtra le proprietà CSS inline: rimuove tutto ciò che inizia con mso-
      const style = el.getAttribute('style');
      if (style) {
        const filteredStyle = style
          .split(';')
          .map(s => s.trim())
          .filter(s => s.length > 0 && !s.toLowerCase().startsWith('mso-'))
          .join('; ');
        if (filteredStyle) {
          el.setAttribute('style', filteredStyle);
        } else {
          el.removeAttribute('style');
        }
      }

      // Rimuove attributi non necessari provenienti da Word
      el.removeAttribute('lang');
      el.removeAttribute('xml:lang');
      el.removeAttribute('xmlns');
    });

    return doc.body.innerHTML;
  }
}
